
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import PmsSystemCard from "./components/PmsSystemCard";
import SettingsCard from "./components/SettingsCard";
import { KeyValuePair } from "./components/SettingsTable";
import PmsIntegrationDialog from "./components/PmsIntegrationDialog";
import RoomCategoriesCard from "./components/RoomCategoriesCard";
import HotelsCard from "./components/HotelsCard";
import { AvailableIntegration, getAvailableIntegrations, Integration } from "@/api/IntegrationsAPI";

interface PmsTabProps {
  propertyId: string;
  currentIntegration?: Integration | null;
  availableIntegrations?: AvailableIntegration[];
}

const PmsTab: React.FC<PmsTabProps> = ({ 
  propertyId, 
  currentIntegration, 
  availableIntegrations = [] 
}) => {
  const { toast } = useToast();
  const [isPmsDialogOpen, setIsPmsDialogOpen] = useState(false);
  
  // State for PMS settings
  const [pmsSettings, setPmsSettings] = useState<KeyValuePair[]>([
    { id: "1", key: "PMS_URL", value: "https://pms.example.com/api", description: "PMS API URL" },
    { id: "2", key: "PMS_TOKEN", value: "************", description: "PMS API Token" },
  ]);

  // State for new PMS setting
  const [newPmsSetting, setNewPmsSetting] = useState<{
    key: string;
    value: string;
    description: string;
  }>({
    key: "",
    value: "",
    description: "",
  });

  const handleAddPmsSetting = () => {
    if (!newPmsSetting.key || !newPmsSetting.value) {
      toast({
        title: "Error",
        description: "Key and Value are required fields.",
        variant: "destructive",
      });
      return;
    }

    const newSetting: KeyValuePair = {
      id: Date.now().toString(),
      ...newPmsSetting,
    };

    setPmsSettings([...pmsSettings, newSetting]);
    setNewPmsSetting({ key: "", value: "", description: "" });

    toast({
      title: "Success",
      description: "PMS setting added successfully",
    });
  };

  const handleAddPmsIntegration = () => {
    setIsPmsDialogOpen(true);
  };

  const handleDeletePmsIntegration = () => {
    // Show confirmation toast
    toast({
      title: "Confirm Deletion",
      description: `Are you sure you want to delete the ${currentIntegration?.name} integration?`,
      action: (
        <div className="flex space-x-2">
          <button 
            className="rounded bg-destructive px-3 py-2 text-sm font-medium text-destructive-foreground"
            onClick={() => confirmDeleteIntegration()}
          >
            Delete
          </button>
          <button 
            className="rounded bg-secondary px-3 py-2 text-sm font-medium"
            onClick={() => toast({ title: "Cancelled", description: "Integration deletion cancelled" })}
          >
            Cancel
          </button>
        </div>
      ),
    });
  };

  const confirmDeleteIntegration = () => {
    // Here you would call the API to delete the integration
    toast({
      title: "Integration Deleted",
      description: `The ${currentIntegration?.name} integration has been removed.`,
    });
    
    // In a real implementation, you would refresh the integrations or update the state
    // This would typically be handled by the parent component after API call
  };

  const handleRmsCloudIntegration = () => {
    toast({
      title: "RMS Cloud Integration",
      description: "Connecting to RMS Cloud...",
    });
    setIsPmsDialogOpen(false);
  };

  const handleMewsIntegration = () => {
    toast({
      title: "MEWS Integration",
      description: "Connecting to MEWS...",
    });
    setIsPmsDialogOpen(false);
  };

  const handleCloudbedIntegration = () => {
    // Similar to the Xero/MYOB OAuth2 flow in AccountingTab
    const cloudbedsIntId = import.meta.env.VITE_CLOUDBEDS_ID // This would come from an environment variable or API
    
    // Find the integration in the available integrations
    const integration = availableIntegrations.find(
      (integration) => integration.id === cloudbedsIntId
    );
    
    if (!integration) {
      toast({
        title: "Error",
        description: "Cloudbeds integration not found.",
        variant: "destructive",
      });
      return;
    }
    
    toast({
      title: "Cloudbeds Integration",
      description: "Starting OAuth2 flow with Cloudbeds...",
    });
    
    // Redirect to Cloudbeds OAuth2 authorization URL
    window.location.href = `${integration.url}&state=${integration.id}:${propertyId}`//`https://hotels.cloudbeds.com/api/v1.1/oauth?client_id=${integration.clientId}&redirect_uri=${integration.callback}&response_type=code&scope=read:hotel read:reservation&state=${integration.id}:${propertyId}`;
    
    setIsPmsDialogOpen(false);
  };

  return (
    <div className="space-y-6">
      <PmsSystemCard 
        onAddIntegration={handleAddPmsIntegration} 
        onDeleteIntegration={handleDeletePmsIntegration}
        currentIntegration={currentIntegration}
      />
      
      <PmsIntegrationDialog
        open={isPmsDialogOpen}
        onOpenChange={setIsPmsDialogOpen}
        onRmsCloudIntegration={handleRmsCloudIntegration}
        onMewsIntegration={handleMewsIntegration}
        onCloudbedIntegration={handleCloudbedIntegration}
      />
      
      {/* <SettingsCard
        title="PMS Settings"
        description="Manage property management system integration settings"
        settings={pmsSettings}
        newSetting={newPmsSetting}
        onNewSettingKeyChange={(key) => setNewPmsSetting({ ...newPmsSetting, key })}
        onNewSettingValueChange={(value) => setNewPmsSetting({ ...newPmsSetting, value })}
        onNewSettingDescriptionChange={(description) => setNewPmsSetting({ ...newPmsSetting, description })}
        onAddSetting={handleAddPmsSetting}
        idPrefix="pms"
        tableCaption="Property management system configuration settings"
      /> */}

      <HotelsCard propertyId={propertyId} currentIntegration={currentIntegration} />

      <RoomCategoriesCard propertyId={propertyId} currentIntegration={currentIntegration} />
    </div>
  );
};

export default PmsTab;
